import React from "react";
import PropTypes from "prop-types";

import { formatShortDate } from "modules/patient/sections/chiefComplaints/dateHelpers";
import { readingTag } from "modules/patient/sections/labInvestigations/data";
import { rangeToText } from "modules/patient/sections/labInvestigations/LabInvestigationForm";
import {
  ChevronDownIcon,
  FlaskIcon,
  HeaderSearchIcon,
} from "modules/patient/sections/labInvestigations/labIcons";
import { Sparkle } from "modules/patient/sections/controls";

/**
 * Lab investigations on a phone — the Figma "Lab investigations" mobile frame.
 *
 *   Lab investigations
 *   49 entries
 *   [ search ]
 *   ┌ Heart rate (resting)            ↑ 104 bpm ┐   a test with no panel is
 *   └ Ref 60–100 bpm · 17 Jun 2026              ┘   a card of its own
 *   ┌ Complete blood count (CBC)             ^ ┐   a panel is ONE card, its
 *   │ 12 entries  [1 flagged]                   │   header folds it
 *   ├ RBC count                     5.1 ×10⁶/µL │
 *   └ Ref 4.5–5.9 ×10⁶/µL · 23 May 2026         ┘
 *
 * WHY A RENDER AND NOT CSS OVER THE TABLE, unlike every other section: the
 * card's second line is the REFERENCE RANGE, and the desktop row never draws
 * it — its Range column prints the flag ("High"). There is nothing in that DOM
 * to restyle into "Ref 60–100 bpm".
 *
 * PRESENTATIONAL. The section still owns everything that decides what is
 * shown — the search box writes its `search`, the panels fold through its
 * `openPanels`, and the rows arrive already filtered and grouped. Nothing here
 * fetches, filters, or writes.
 *
 * READ-ONLY ROWS, by decision (2026-09-17): no chevron and no tap. Desktop
 * removed row clicks from this table, and View trend is hidden on mobile, so
 * a reading has nowhere to go that a phone should offer.
 */

const isFlagged = (flag) => flag === "low" || flag === "high" || flag === "abnormal";

/** One test's latest reading — the row the desktop table draws, as two lines. */
const Reading = ({ tg }) => {
  const latest = tg.latest;
  const test = latest.test || {};
  const tag = readingTag(test);

  /* "Ref 60–100 bpm · 17 Jun 2026", each half dropped when it has nothing to
     say. The range goes through the form's own rangeToText, so a range reads
     the same here as in "Reference range · Auto". */
  const range = rangeToText(test.refRange);
  const ref = range ? `Ref ${range}${test.unit ? ` ${test.unit}` : ""}` : "";
  const sub = [ref, formatShortDate(latest.testDate)].filter(Boolean).join(" · ");

  return (
    <div
      className={`zym-lab-row${isFlagged(latest.flag) ? " is-flagged" : ""}${
        latest._captured ? " is-captured" : ""
      }`}
    >
      <div className="zym-lab-main">
        <span className="zym-lab-name">
          {latest._capturedIsAi ? (
            <span className="cc-spark" aria-label="AI captured" title="AI captured">
              <Sparkle />
            </span>
          ) : null}
          {test.name}
          {tag ? <span className="li-tag">{tag}</span> : null}
        </span>
        {sub ? <span className="zym-lab-sub">{sub}</span> : null}
      </div>

      <span className={`zym-lab-value is-${latest.flag || "unknown"}`}>
        {latest.flag === "high" && (
          <span className="zym-lab-arr" aria-label="High">
            ↑
          </span>
        )}
        {latest.flag === "low" && (
          <span className="zym-lab-arr" aria-label="Low">
            ↓
          </span>
        )}
        <span className="zym-lab-num">{latest.value}</span>
        {test.unit ? <span className="zym-lab-unit">{test.unit}</span> : null}
      </span>
    </div>
  );
};
Reading.propTypes = {
  tg: PropTypes.shape({ key: PropTypes.string, latest: PropTypes.object.isRequired }).isRequired,
};

const MobileLabList = ({
  count,
  hasRows,
  search,
  onSearch,
  looseTests,
  groups,
  isPanelOpen,
  onTogglePanel,
  testsOf,
}) => (
  <div className="zym-lab">
    <header className="zym-lab-head">
      <h2 className="zym-lab-title">Lab investigations</h2>
      <p className="zym-lab-count">
        {count === 0 ? "No entries" : `${count} ${count === 1 ? "entry" : "entries"}`}
      </p>
    </header>

    {/* No add button in the header: the pinned bar below the list carries it,
        as on every other tab, and it opens the same Digitise report modal. */}

    {!hasRows ? (
      <div className="zym-lab-empty">
        <span className="li3-empty-medallion" aria-hidden="true">
          <FlaskIcon />
        </span>
        <p className="zym-lab-empty-title">No lab investigations yet</p>
        <p className="zym-lab-empty-hint">
          We haven&rsquo;t recorded any labs for this patient. Add the first investigation to
          start tracking results over time.
        </p>
      </div>
    ) : (
      <>
        <label className="zym-lab-search">
          <span className="zym-lab-search-icon" aria-hidden="true">
            <HeaderSearchIcon />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search by name or date…"
            aria-label="Search lab investigations"
          />
        </label>

        {count === 0 ? (
          <p className="zym-lab-nomatch">Nothing matches that search.</p>
        ) : (
          <div className="zym-lab-cards">
            {looseTests.map((tg) => (
              <div className="zym-lab-card" key={tg.key}>
                <Reading tg={tg} />
              </div>
            ))}

            {groups.map((group) => {
              const open = isPanelOpen(group.key);
              return (
                <div className="zym-lab-card is-panel" key={group.key}>
                  <button
                    type="button"
                    className={`zym-lab-panel-head${open ? " is-open" : ""}`}
                    aria-expanded={open}
                    onClick={() => onTogglePanel(group.key)}
                  >
                    <span className="zym-lab-panel-titles">
                      <span className="zym-lab-panel-name">{group.panel}</span>
                      {/* The desktop band's own pills, so their tones come with
                          them rather than being restated here. */}
                      <span className="zym-lab-pills">
                        <span className="li3-pill">
                          {group.testCount} {group.testCount === 1 ? "entry" : "entries"}
                        </span>
                        {group.flaggedCount > 0 && (
                          <span className="li3-pill is-flagged">
                            <span className="li3-pill-dot" aria-hidden="true" />
                            {group.flaggedCount} flagged
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="zym-lab-chev" aria-hidden="true">
                      <ChevronDownIcon />
                    </span>
                  </button>

                  {open && (
                    <div className="zym-lab-rows">
                      {testsOf(group).map((tg) => (
                        <Reading key={tg.key} tg={tg} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    )}
  </div>
);

MobileLabList.propTypes = {
  /** Distinct tests after the search — the number the desktop header shows. */
  count: PropTypes.number.isRequired,
  /** Whether the patient has any readings at all, search aside. */
  hasRows: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  looseTests: PropTypes.array.isRequired,
  groups: PropTypes.array.isRequired,
  isPanelOpen: PropTypes.func.isRequired,
  onTogglePanel: PropTypes.func.isRequired,
  /** A panel's readings grouped by test, latest first — the section's groupByTest. */
  testsOf: PropTypes.func.isRequired,
};

export default MobileLabList;
